import React from 'react';
import { z } from 'zod';
import { useForm, Form, FormItem, FormLabel, FormControl, FormMessage } from './Form';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './Dialog';
import { Button } from './Button';
import { Input } from './Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select';
import { useCreateFile } from '../helpers/useFilesApi';
import { toast } from 'sonner';

const createFileSchema = z.object({
  fileName: z.string().min(1, 'File name is required'),
  filePath: z.string().min(1, 'File path is required'), // We'll default this to root "/"
  language: z.string().min(1, 'Language is required'),
});

interface CreateFileDialogProps {
  sessionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultPath?: string;
}

const LANGUAGES = [
  { value: 'javascript', label: 'JavaScript' },
  { value: 'typescript', label: 'TypeScript' },
  { value: 'python', label: 'Python' },
  { value: 'html', label: 'HTML' },
  { value: 'css', label: 'CSS' },
  { value: 'json', label: 'JSON' },
  { value: 'markdown', label: 'Markdown' },
  { value: 'sql', label: 'SQL' },
  { value: 'java', label: 'Java' },
  { value: 'go', label: 'Go' },
  { value: 'rust', label: 'Rust' },
];

export const CreateFileDialog = ({
  sessionId,
  open,
  onOpenChange,
  defaultPath = '/',
}: CreateFileDialogProps) => {
  const createFile = useCreateFile();

  const form = useForm({
    defaultValues: {
      fileName: '',
      filePath: defaultPath,
      language: 'javascript',
    },
    schema: createFileSchema,
  });

  // Track whether user manually changed language so we don't override it after that
  const [languageManual, setLanguageManual] = React.useState(false);

  // Small helper copied from CodeEditor to detect language from filename extension
  const detectLanguageFromFileName = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    const extMap: Record<string, string> = {
      tsx: 'typescript',
      ts: 'typescript',
      js: 'javascript',
      jsx: 'javascript',
      py: 'python',
      rs: 'rust',
      go: 'go',
      html: 'html',
      css: 'css',
      json: 'json',
      md: 'markdown',
      sql: 'sql',
      java: 'java',
      cs: 'csharp',
      cpp: 'cpp',
      rb: 'ruby',
      php: 'php',
      vue: 'vue',
      svelte: 'svelte',
    };
    return extMap[ext] || '';
  };

  // Auto-detect language when the filename changes (unless user manually set language)
  React.useEffect(() => {
    const fileName = form.values.fileName || '';
    if (!languageManual && fileName && fileName.includes('.')) {
      const detected = detectLanguageFromFileName(fileName);
      if (detected && detected !== form.values.language) {
        form.setValues((prev) => ({ ...prev, language: detected }));
      }
    }
  }, [form.values.fileName, languageManual]);

  // Update form when defaultPath changes
  React.useEffect(() => {
    if (open && defaultPath) {
      form.setValues((prev) => ({ ...prev, filePath: defaultPath }));
    }
  }, [open, defaultPath]);

  const onSubmit = (values: z.infer<typeof createFileSchema>) => {
    const content = '// Start coding here...';
    // Sanitize filename/path (strip accidental surrounding quotes)
    const sanitize = (s: string) => s.replace(/^\s*"|"\s*$/g, '').trim();
    const fileName = sanitize(values.fileName);
    const filePath = sanitize(values.filePath || '/');

    (async () => {
      try {
        console.log('[CreateFileDialog] Creating file (DB):', {
          sessionId,
          fileName,
          filePath,
          language: values.language,
        });
        const res = await createFile.mutateAsync({
          sessionId,
          fileName,
          filePath,
          language: values.language,
          content,
        });

        console.log('[CreateFileDialog] File created in DB:', res.file);

        toast.success('File created');
        onOpenChange(false);
        form.setValues({ fileName: '', filePath: '/', language: 'javascript' });
      } catch (err: any) {
        console.error('[CreateFileDialog] Failed to create file:', err);
        const msg = err?.message || String(err) || 'Failed to create file';
        toast.error(msg);
      }
    })();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New File</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormItem name="fileName">
              <FormLabel>File Name</FormLabel>
              <FormControl>
                <Input
                  placeholder="e.g., index.ts"
                  value={form.values.fileName}
                  onChange={(e) =>
                    form.setValues((prev) => ({ ...prev, fileName: e.target.value }))
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>

            <FormItem name="language">
              <FormLabel>Language</FormLabel>
              <Select
                value={form.values.language}
                onValueChange={(val) => {
                  setLanguageManual(true);
                  form.setValues((prev) => ({ ...prev, language: val }));
                }}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {LANGUAGES.map((lang) => (
                    <SelectItem key={lang.value} value={lang.value}>
                      {lang.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>

            <DialogFooter>
              <Button variant="secondary" type="button" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createFile.isPending}>
                Create File
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
